import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { FlexBox } from '../box';
import { styled } from '../styled';
import { TextField } from '../textfield';
import { buildSvg, extractTextFromSvgDataUrl, getNextId, HelperText, svgToDataUrl, useBase64Font, Wrapper } from './helpers';
const MAX_FONT_SIZE = 48;
const MIN_FONT_SIZE = 16;
const H_PADDING = 20;
const WATERMARK_BREAKPOINT_LG = 450;
const WATERMARK_BREAKPOINT_MD = 420;
export const TypeSignature = ({ width, height, textFieldLabel, textFieldPlaceholder, infoText, watermark = 'Generated signature', onChange, required, error, initialState, disabled }) => {
    const extractedText = extractTextFromSvgDataUrl(initialState?.dataUrl, watermark);
    const [value, setValue] = useState(() => extractedText ?? '');
    const [fontSize, setFontSize] = useState(MAX_FONT_SIZE);
    const measureTextRef = useRef(null);
    const { spacing } = useTheme();
    const errorId = getNextId('signature-svg-error-id');
    const hasError = Boolean(error);
    const [pacificoUrl, setPacificoUrl] = useState('');
    const base64Font = useBase64Font(pacificoUrl);
    const innerHeightMargin = parseFloat(spacing(5));
    const innerWidthMargin = parseFloat(spacing(4));
    const innerWidth = width - innerWidthMargin;
    const innerHeight = height - innerHeightMargin - parseFloat(spacing(15));
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const { default: url } = await import(
                // eslint-disable-next-line import/no-internal-modules
                '@fontsource/pacifico/files/pacifico-latin-400-normal.woff2');
                if (!cancelled) {
                    setPacificoUrl(url);
                }
            }
            catch (err) {
                console.error('Failed to load Pacifico font', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        if (!value && initialState?.dataUrl) {
            const fromSvg = extractTextFromSvgDataUrl(initialState.dataUrl, watermark);
            if (fromSvg) {
                setValue(fromSvg);
            }
        }
    }, [initialState?.dataUrl, watermark, value]);
    useEffect(() => {
        let cancelled = false;
        const measureAndFit = async () => {
            if (cancelled) {
                return;
            }
            const signatureText = measureTextRef.current;
            if (!signatureText || !value) {
                setFontSize(MAX_FONT_SIZE);
                return;
            }
            signatureText.setAttribute('font-size', String(MAX_FONT_SIZE));
            signatureText.textContent = value;
            const bbox = signatureText.getBBox();
            const maxWidth = Math.max(1, innerWidth - H_PADDING);
            const measuredWidth = Math.max(1, bbox.width);
            const scale = Math.min(1, maxWidth / measuredWidth);
            const nextFontSize = Math.max(MIN_FONT_SIZE, Math.floor(MAX_FONT_SIZE * scale));
            setFontSize(nextFontSize);
        };
        void measureAndFit();
        return () => {
            cancelled = true;
        };
    }, [value, innerWidth]);
    const watermarkTop = parseFloat(width > WATERMARK_BREAKPOINT_LG
        ? spacing(3)
        : width > WATERMARK_BREAKPOINT_MD
            ? spacing(4)
            : spacing(5));
    const WatermarkSvg = () => !hasError ? (_jsx("text", { x: innerWidth - 10, y: watermarkTop, textAnchor: "end", fontFamily: "", dominantBaseline: "hanging", fontSize: 12, opacity: 0.5, children: watermark })) : null;
    function handleChange(v) {
        setValue(v);
        if (!v.trim()) {
            onChange?.(undefined);
            return;
        }
        if (!base64Font) {
            return;
        }
        const svg = buildSvg(v, width, height, fontSize, base64Font, watermark);
        const dataUrl = svgToDataUrl(svg);
        onChange?.({ dataUrl });
    }
    return (_jsxs(Wrapper, { gap: 2, stack: true, width: width, height: height, padding: [3, 2], maxHeight: height, children: [_jsx(TextField, { label: textFieldLabel, placeholder: textFieldPlaceholder, value: disabled ? textFieldPlaceholder : value, onChange: handleChange, clearable: !disabled, autoFocus: true, disabled: disabled, error: hasError, "aria-describedby": errorId, required: required }), _jsxs("svg", { style: {
                    position: 'absolute',
                    visibility: 'hidden',
                    height: 0,
                    overflow: 'visible'
                }, xmlns: "http://www.w3.org/2000/svg", children: [_jsx(WatermarkSvg, {}), _jsx("text", { ref: measureTextRef, fontFamily: "Pacifico, cursive" })] }), _jsx(SignatureArea, { width: innerWidth, height: innerHeight, error: hasError, children: _jsxs("svg", { width: innerWidth, height: innerHeight, viewBox: `0 0 ${innerWidth} ${innerHeight}`, xmlns: "http://www.w3.org/2000/svg", preserveAspectRatio: "xMidYMid meet", children: [_jsx(WatermarkSvg, {}), _jsx("text", { x: "50%", y: "50%", textAnchor: "middle", dominantBaseline: "central", fontFamily: "Pacifico, cursive", fontSize: fontSize, fill: "black", children: value })] }) }), _jsx(HelperText, { disabled: disabled, error: hasError ? true : undefined, id: errorId, children: error ? error : infoText })] }));
};
const SignatureArea = styled(FlexBox)(({ theme, error }) => ({
    border: '1px dashed',
    borderColor: error
        ? theme.palette.error._states['outlined-border']
        : theme.components?.input?.outlined?.['enabled-border'],
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    background: error
        ? theme.components?.alert?.error?.background
        : theme.palette.background.paper
}));
